<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import AgenteIaAPI from 'dashboard/api/agenteIa';

defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
});

const { t } = useI18n();
const route = useRoute();
const status = ref(null);

// Menu do modulo em tres grupos, na ordem de quem esta comecando.
const GRUPOS = [
  {
    chave: 'COMECAR',
    itens: [
      { name: 'agente_ia_inicio', label: 'INICIO', icone: 'i-lucide-house' },
      {
        name: 'agente_ia_montar',
        label: 'MONTAR',
        icone: 'i-lucide-wand-sparkles',
      },
      {
        name: 'agente_ia_chaves',
        label: 'CHAVES',
        icone: 'i-lucide-key-round',
      },
      {
        name: 'agente_ia_testar',
        label: 'TESTAR',
        icone: 'i-lucide-message-circle',
      },
    ],
  },
  {
    chave: 'CONFIGURAR',
    itens: [
      {
        name: 'agente_ia_habilidades',
        label: 'HABILIDADES',
        icone: 'i-lucide-list-checks',
      },
      {
        name: 'agente_ia_comportamento',
        label: 'COMPORTAMENTO',
        icone: 'i-lucide-sliders-horizontal',
      },
      { name: 'agente_ia_base', label: 'BASE', icone: 'i-lucide-book-open' },
      { name: 'agente_ia_caixas', label: 'CAIXAS', icone: 'i-lucide-inbox' },
      {
        name: 'agente_ia_operador',
        label: 'OPERADOR',
        icone: 'i-lucide-kanban',
        modulo: 'operador_funil',
      },
    ],
  },
  {
    chave: 'ACOMPANHAR',
    itens: [
      {
        name: 'agente_ia_conversas',
        label: 'CONVERSAS',
        icone: 'i-lucide-chart-column',
      },
      { name: 'agente_ia_prompt', label: 'PROMPT', icone: 'i-lucide-eye' },
      { name: 'agente_ia_claude', label: 'CLAUDE', icone: 'i-lucide-plug' },
      {
        name: 'agente_ia_plano',
        label: 'PLANO',
        icone: 'i-lucide-badge-dollar-sign',
      },
    ],
  },
];

const grupos = computed(() =>
  GRUPOS.map(grupo => ({
    ...grupo,
    // Itens de modulo a parte so aparecem quando o Super Admin liga o modulo.
    itens: grupo.itens
      .filter(item => !item.modulo || status.value?.vinculo?.[item.modulo])
      .map(item => ({
        ...item,
        ativo: route.name === item.name,
        to: { name: item.name, params: { accountId: route.params.accountId } },
      })),
  }))
);

const carregarStatus = async () => {
  try {
    status.value = (await AgenteIaAPI.status()).data;
  } catch (e) {
    status.value = null;
  }
};

onMounted(carregarStatus);
// As telas avisam quando mudam algo que aparece no cabecalho (chave, modo).
defineExpose({ carregarStatus });

const emProducao = computed(() => status.value?.persona?.mode === 'production');
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-background">
    <div class="flex flex-col w-full max-w-6xl gap-6 px-4 py-6 mx-auto sm:px-6">
      <header
        class="flex flex-col gap-4 p-5 rounded-xl sm:flex-row sm:items-center sm:justify-between bg-n-solid-1 outline outline-1 outline-n-weak"
      >
        <div class="flex items-center gap-3">
          <span
            class="flex items-center justify-center rounded-xl size-11 bg-n-blue-3 text-n-blue-11"
          >
            <span class="i-lucide-bot size-6" />
          </span>
          <div class="flex flex-col">
            <span
              class="text-xs font-medium tracking-wide uppercase text-n-slate-11"
            >
              {{ t('AGENTE_IA.MENU') }}
            </span>
            <span class="text-base font-medium text-n-slate-12">
              {{ status?.persona?.name || t('AGENTE_IA.CARREGANDO') }}
            </span>
          </div>
        </div>
        <div v-if="status?.persona" class="flex flex-wrap gap-2">
          <span
            class="px-2.5 py-1 text-xs font-medium rounded-full"
            :class="
              emProducao
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-amber-3 text-n-amber-11'
            "
          >
            {{ t(`AGENTE_IA.MODO.${emProducao ? 'PRODUCAO' : 'TESTE'}`) }}
          </span>
          <span
            class="px-2.5 py-1 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-12"
          >
            {{ t(`AGENTE_IA.PLANOS.${status.vinculo.plano.toUpperCase()}`) }}
          </span>
          <span
            v-if="!status.persona.com_chave"
            class="px-2.5 py-1 text-xs font-medium rounded-full bg-n-ruby-3 text-n-ruby-11"
          >
            {{ t('AGENTE_IA.SEM_CHAVE') }}
          </span>
        </div>
      </header>

      <div class="grid gap-6 lg:grid-cols-[15rem_1fr]">
        <nav class="flex flex-col gap-4">
          <div
            v-for="grupo in grupos"
            :key="grupo.chave"
            class="flex flex-col gap-1"
          >
            <span
              class="px-3 text-xs font-medium tracking-wide uppercase text-n-slate-10"
            >
              {{ t(`AGENTE_IA.GRUPOS.${grupo.chave}`) }}
            </span>
            <div class="flex flex-wrap gap-1 lg:flex-col">
              <router-link
                v-for="item in grupo.itens"
                :key="item.name"
                :to="item.to"
                class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg"
                :class="
                  item.ativo
                    ? 'bg-n-blue-3 text-n-blue-11 font-medium'
                    : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
                "
              >
                <span :class="item.icone" class="size-4 shrink-0" />
                {{ t(`AGENTE_IA.NAV.${item.label}`) }}
              </router-link>
            </div>
          </div>
        </nav>

        <main
          class="flex flex-col min-w-0 gap-6 p-5 rounded-xl sm:p-6 bg-n-solid-1 outline outline-1 outline-n-weak"
        >
          <div class="flex flex-col gap-1">
            <h1 class="text-xl font-medium text-n-slate-12">{{ title }}</h1>
            <p v-if="description" class="text-sm text-n-slate-11">
              {{ description }}
            </p>
          </div>
          <p
            v-if="status?.vinculo?.operado_externamente"
            role="status"
            class="p-3 text-sm rounded-xl bg-n-amber-3 text-n-amber-11"
          >
            {{ t('AGENTE_IA.OPERADO_MOTOR.AVISO') }}
          </p>
          <slot />
        </main>
      </div>
    </div>
  </section>
</template>
